import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";

const PORTRAIT = "/alekz-portrait.jpg";

export default function Home() {
  const heroRef = useRef(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;

    let rafId = 0;
    let targetX = 0.5;
    let targetY = 0.5;
    let currentX = 0.5;
    let currentY = 0.5;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      targetX = (e.clientX - rect.left) / rect.width;
      targetY = (e.clientY - rect.top) / rect.height;
    };

    const tick = () => {
      currentX += (targetX - currentX) * 0.08;
      currentY += (targetY - currentY) * 0.08;
      el.style.setProperty("--mx", currentX.toFixed(4));
      el.style.setProperty("--my", currentY.toFixed(4));
      rafId = requestAnimationFrame(tick);
    };

    el.addEventListener("mousemove", onMove);
    rafId = requestAnimationFrame(tick);

    return () => {
      el.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="home-hero"
      style={{ "--mx": 0.5, "--my": 0.5 }}
    >
      <SEO
        path="/"
        description="ALEKZ — the artist project of Alex Soto (Alekz Soto, Alexander Soto). New York-based DJ, techno producer and creative."
      />

      {/* Visually hidden semantic block — helps crawlers index every name variant */}
      <h1 className="sr-only">
        ALEKZ — Alex Soto (also known as Alekz Soto and Alexander Soto). DJ,
        techno producer and New York-based creative. Official website of ALEKZ.
      </h1>
      {/* === Interactive chrome spotlight — follows the cursor === */}
      <div
        aria-hidden
        data-testid="cursor-glow"
        className="pointer-events-none absolute inset-0 z-0 hidden md:block"
        style={{
          background:
            "radial-gradient(420px circle at calc(var(--mx) * 100%) calc(var(--my) * 100%), rgba(217,217,217,0.10), rgba(217,217,217,0.04) 35%, transparent 70%)",
          mixBlendMode: "screen",
          transition: "opacity 0.3s ease",
        }}
      />

      {/* === Drifting silver particles (slow ambient motion) === */}
      <div
        aria-hidden
        data-testid="ambient-particles"
        className="pointer-events-none absolute inset-0 z-0 hidden md:block"
      >
        {[
          { l: 18, t: 32, d: 0, s: 14 },
          { l: 12, t: 70, d: 1.5, s: 18 },
          { l: 86, t: 22, d: 0.8, s: 16 },
          { l: 92, t: 58, d: 2.2, s: 20 },
          { l: 78, t: 84, d: 1.1, s: 15 },
          { l: 26, t: 86, d: 2.8, s: 17 },
        ].map((p, i) => (
          <span
            key={i}
            className="absolute w-[3px] h-[3px] rounded-full bg-[var(--silver)]/70"
            style={{
              left: `${p.l}%`,
              top: `${p.t}%`,
              animation: `particle-drift ${p.s}s ease-in-out ${p.d}s infinite`,
              filter: "blur(0.3px)",
              boxShadow: "0 0 6px rgba(217,217,217,0.5)",
            }}
          />
        ))}
      </div>
      {/* === Abstract silver chrome decorations (left/right dead zones) === */}

      {/* Wireframe chrome sphere — far left, partially off-screen */}
      <svg
        aria-hidden
        data-testid="deco-sphere"
        className="pointer-events-none absolute top-1/2 -left-[10vw] w-[34vw] h-[34vw] max-w-[520px] max-h-[520px] fade-in hidden md:block"
        style={{
          animationDelay: "0.45s",
          transform:
            "translate3d(calc((var(--mx) - 0.5) * 30px), calc((var(--my) - 0.5) * 30px - 50%), 0)",
          transition: "transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
        viewBox="0 0 200 200"
      >
        <ellipse cx="100" cy="100" rx="96" ry="96" fill="none" stroke="rgba(217,217,217,0.16)" strokeWidth="0.35" />
        <ellipse cx="100" cy="100" rx="96" ry="28" fill="none" stroke="rgba(217,217,217,0.26)" strokeWidth="0.3" transform="rotate(-14 100 100)" />
        <ellipse cx="100" cy="100" rx="96" ry="58" fill="none" stroke="rgba(217,217,217,0.18)" strokeWidth="0.3" transform="rotate(-14 100 100)" />
        <ellipse cx="100" cy="100" rx="96" ry="84" fill="none" stroke="rgba(217,217,217,0.12)" strokeWidth="0.3" transform="rotate(-14 100 100)" />
        <ellipse cx="100" cy="100" rx="22" ry="96" fill="none" stroke="rgba(217,217,217,0.2)" strokeWidth="0.3" transform="rotate(-14 100 100)" />
        <ellipse cx="100" cy="100" rx="54" ry="96" fill="none" stroke="rgba(217,217,217,0.13)" strokeWidth="0.3" transform="rotate(-14 100 100)" />
        <circle cx="100" cy="100" r="1.2" fill="rgba(217,217,217,0.7)" />
      </svg>

      {/* Chrome dial — top-right */}
      <svg
        aria-hidden
        data-testid="deco-dial"
        className="pointer-events-none absolute top-[24%] right-[8%] w-16 h-16 lg:w-20 lg:h-20 fade-in hidden md:block"
        style={{
          animationDelay: "0.8s",
          transform:
            "translate3d(calc((var(--mx) - 0.5) * -40px), calc((var(--my) - 0.5) * -40px), 0)",
          transition: "transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
        viewBox="0 0 60 60"
      >
        <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(217,217,217,0.26)" strokeWidth="0.4" />
        <circle cx="30" cy="30" r="14" fill="none" stroke="rgba(217,217,217,0.5)" strokeWidth="0.4" />
        <circle cx="30" cy="30" r="2" fill="rgba(217,217,217,0.9)" />
        <line x1="30" y1="2" x2="30" y2="6" stroke="rgba(217,217,217,0.6)" strokeWidth="0.4" />
        <line x1="30" y1="54" x2="30" y2="58" stroke="rgba(217,217,217,0.6)" strokeWidth="0.4" />
        <line x1="2" y1="30" x2="6" y2="30" stroke="rgba(217,217,217,0.6)" strokeWidth="0.4" />
        <line x1="54" y1="30" x2="58" y2="30" stroke="rgba(217,217,217,0.6)" strokeWidth="0.4" />
      </svg>

      {/* Vertical chrome glow bar — bottom-right */}
      <div
        aria-hidden
        data-testid="deco-bar"
        className="pointer-events-none absolute bottom-[14%] right-[5%] w-px h-28 fade-in hidden md:block"
        style={{
          background:
            "linear-gradient(to bottom, transparent, rgba(217,217,217,0.55), transparent)",
          animationDelay: "1s",
        }}
      />

      {/* Tiny silver constellation dots — top-left, asymmetric */}
      <div
        aria-hidden
        data-testid="deco-dots"
        className="pointer-events-none absolute top-[22%] left-[6%] fade-in hidden md:block"
        style={{ animationDelay: "1.1s" }}
      >
        <div className="flex flex-col items-start gap-3">
          <div className="w-1 h-1 rounded-full bg-[var(--silver)]/80" />
          <div className="w-px h-10 bg-[var(--silver)]/30 ml-[1px]" />
          <div className="w-1 h-1 rounded-full bg-[var(--silver)]/40" />
        </div>
      </div>

      {/* Portrait — centerpiece, full-bleed and centered behind type */}
      <div
        className="absolute inset-0 flex items-center justify-center fade-in"
        style={{ animationDelay: "0.15s" }}
        data-testid="portrait-wrap"
      >
        <div className="relative w-[88vw] sm:w-[70vw] md:w-[58vw] lg:w-[50vw] xl:w-[44vw] max-w-[760px] aspect-square -translate-y-10 sm:-translate-y-14 md:-translate-y-16">
          {/* Soft silver halo behind portrait */}
          <div
            aria-hidden
            className="absolute -inset-10 rounded-full"
            style={{
              background:
                "radial-gradient(ellipse at 50% 38%, rgba(217,217,217,0.12) 0%, rgba(217,217,217,0.04) 35%, transparent 70%)",
              filter: "blur(40px)",
            }}
          />
          <img
            src={PORTRAIT}
            alt="ALEKZ"
            className="portrait-mask absolute inset-0 w-full h-full object-contain drift"
            style={{ objectPosition: "center center" }}
            data-testid="hero-portrait"
            draggable={false}
          />
        </div>
      </div>

      {/* Center wordmark overlay — type sits on top of portrait */}
      <div
        className="relative z-20 flex flex-col items-center justify-center min-h-screen px-6"
        data-testid="home-wordmark-wrap"
      >
        <h1
          className="text-white text-center leading-[0.9] rise select-none translate-y-16 sm:translate-y-20 md:translate-y-24"
          style={{
            fontFamily: "'Montserrat', 'Proxima Nova', sans-serif",
            fontWeight: 400,
            fontSize: "clamp(2.5rem, 9vw, 9rem)",
            letterSpacing: "0.08em",
            textShadow:
              "0 8px 30px rgba(0,0,0,0.65), 0 1px 0 rgba(255,255,255,0.08)",
            mixBlendMode: "screen",
          }}
          data-testid="alekz-mark"
        >
          ALEKZ
        </h1>
      </div>

      {/* Enter chip */}
      <div
        className="absolute z-30 left-1/2 -translate-x-1/2 bottom-[36px] fade-in"
        style={{ animationDelay: "1.3s" }}
      >
        <Link
          to="/music"
          data-testid="enter-cta"
          className="silver-chip inline-flex items-center gap-3 px-5 py-2.5 rounded-full font-mono-wide text-[10px] tracking-[0.5em]"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--silver)]" />
          Enter
        </Link>
      </div>

      {/* Hidden reveal-on-hover link to FEELS RIGHT — bottom left */}
      <Link
        to="/feels-right"
        data-testid="hidden-feels-right"
        aria-label="Feels Right — hidden single"
        className="group absolute z-30 bottom-4 left-4 sm:bottom-6 sm:left-8 lg:left-12 p-3 font-mono-wide text-[9px] tracking-[0.45em]"
      >
        <span className="text-white/0 group-hover:text-white/70 transition-colors duration-500 ease-out">
          FEELS RIGHT
        </span>
      </Link>
    </section>
  );
}
